import { loadStripe } from '@stripe/stripe-js';
import { supabase } from '../lib/supabase';

// Initialize Stripe with publishable key
// Using both possible env var names for compatibility
const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 
  import.meta.env.VITE_STRIPE_PUBLIC_KEY ||
  'pk_test_51RwdSO0GdyORS8iwxQqw8BRbn1T7z4ooFt6WVMgItrLOeiAGKYMKzGnA5bjv2JgzfesbKxsSli3IzuKHkyUx7tZA00jhrIGWlo'
);

// Price IDs for different plans
export const PRICE_IDS = {
  scout: {
    monthly: null,
    annual: null,
  },
  hunter: {
    monthly: import.meta.env.VITE_STRIPE_PRICE_HUNTER_MONTHLY || 'price_1RzWG20GdyORS8iwyYKkMfgV',
    annual: import.meta.env.VITE_STRIPE_PRICE_HUNTER_ANNUAL || 'price_1RzWG30GdyORS8iwVB9JqLMN',
  },
  pro: {
    monthly: import.meta.env.VITE_STRIPE_PRICE_PRO_MONTHLY || 'price_1RzWG40GdyORS8iwkgDSvca1',
    annual: import.meta.env.VITE_STRIPE_PRICE_PRO_ANNUAL || 'price_1RzWG40GdyORS8iw2CfsWp5t',
  },
  enterprise: {
    monthly: import.meta.env.VITE_STRIPE_PRICE_ENTERPRISE_MONTHLY || 'price_1RzWG50GdyORS8iwNudMeWFU',
    annual: import.meta.env.VITE_STRIPE_PRICE_ENTERPRISE_ANNUAL || 'price_1RzWG50GdyORS8iwvRZMFvzg',
  },
};

export interface CheckoutSessionParams {
  priceId: string;
  successUrl?: string;
  cancelUrl?: string;
}

/**
 * Create a Stripe Checkout session and redirect to payment
 */
export async function createCheckoutSession({
  priceId,
  successUrl = `${window.location.origin}/subscription/success`,
  cancelUrl = `${window.location.origin}/pricing`,
}: CheckoutSessionParams) {
  try {
    console.log('Creating payment session for price:', priceId);
    
    // Get the current session to get the user's access token
    const { data: { session } } = await supabase.auth.getSession();
    
    console.log('Session:', session);
    console.log('Access token:', session?.access_token);
    
    // Use payment links directly as they work with current account permissions
    // Payment links are a valid alternative to checkout sessions for subscriptions
    console.log('Creating payment link for subscription');
    
    const paymentLinkResponse = await supabase.functions.invoke('create-payment-link', {
      body: {
        priceId,
      },
    });
    
    if (paymentLinkResponse.error) {
      // If payment link fails, try checkout session as fallback
      console.log('Payment link failed, trying checkout session');
      
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          priceId,
          successUrl,
          cancelUrl,
        },
      });
      
      if (error) {
        throw new Error(`Checkout error: ${error.message}`);
      }
      
      if (!data?.url) {
        throw new Error('Invalid response from checkout session creation');
      }
      
      // Redirect to Stripe Checkout
      window.location.href = data.url;
      return;
    }
    
    if (!paymentLinkResponse.data?.url) {
      throw new Error('Invalid response from payment link creation');
    }
    
    // Redirect to Payment Link
    window.location.href = paymentLinkResponse.data.url;
  } catch (error) {
    console.error('Error creating payment session:', error);
    throw error;
  }
}

/**
 * Get the current user's subscription status
 */
export async function getSubscriptionStatus() {
  try {
    // TODO: Implement actual subscription status check
    // This should call your backend API to get the user's subscription status
    return {
      subscription_status: 'inactive',
      subscription_tier: null,
      subscription_current_period_end: null,
    };
  } catch (error) {
    console.error('Error getting subscription status:', error);
    return null;
  }
}

/**
 * Check if user has an active subscription
 */
export async function hasActiveSubscription(): Promise<boolean> {
  const status = await getSubscriptionStatus();
  return status?.subscription_status === 'active' || status?.subscription_status === 'trialing';
}

/**
 * Get the appropriate price ID based on plan and billing period
 */
export function getPriceId(plan: 'scout' | 'hunter' | 'pro' | 'enterprise', billingPeriod: 'monthly' | 'annual'): string | null {
  return PRICE_IDS[plan]?.[billingPeriod] || null;
}